import type { AudienceCategoryItem, AudienceItem, CountryItem } from '@digiseg-labs/api-client';
import { formatLargeNumber } from './number';
import type { ReachAnalysis, SankeyData, SankeyLink, SankeyNode, SelectedAudience } from '../components/audience-builder/types';

const CORE_AUDIENCE_ORDER = [
  'e1', 'e2', 'e3',
  'h1', 'h2', 'h3', 'h4', 'h5',
  'c1', 'c2', 'c3', 'c4', 'c5',
  'd1', 'd2', 'd3',
  'a1', 'a2',
  'j2', 'j1',
  'f1', 'f2', 'f3',
  'b1', 'b2', 'b3',
  'n1', 'n2', 'n3',
  'g1', 'g2', 'g3', 'g4',
  'k1', 'k2', 'k3',
  'l1', 'l2', 'l3',
];

export const CORE_AUDIENCE_CODES = new Set(CORE_AUDIENCE_ORDER);

const CORE_DIMENSION_ORDER = ['e', 'h', 'c', 'd', 'a', 'j', 'f', 'b', 'n', 'g', 'k', 'l'];

const audienceCodePrefix = (code: string) => (
  code.match(/^[a-z]+/i)?.[0].toLowerCase() ?? code.toLowerCase()
);

const CORE_DIMENSION_COLORS: Record<string, string> = {
  e: 'blue',
  h: 'cyan',
  c: 'teal',
  d: 'green',
  a: 'lime',
  j: 'yellow',
  f: 'amber',
  b: 'orange',
  n: 'tomato',
  g: 'red',
  k: 'ruby',
  l: 'purple',
};

export const AUDIENCE_COLOR_STEPS = [9, 7, 5];

const AUDIENCE_COLOR_INDEX = new Map<string, number>();
const AUDIENCE_PREFIX_COUNTER = new Map<string, number>();
CORE_AUDIENCE_ORDER.forEach((code) => {
  const prefix = audienceCodePrefix(code);
  const nextIndex = AUDIENCE_PREFIX_COUNTER.get(prefix) ?? 0;
  AUDIENCE_PREFIX_COUNTER.set(prefix, nextIndex + 1);
  AUDIENCE_COLOR_INDEX.set(code, nextIndex);
});

type ReachMetricKey = 'num_households' | 'num_persons' | 'num_devices' | 'num_daily_impressions';

export const getAudienceMap = (categories: AudienceCategoryItem[]) => {
  const map = new Map<string, AudienceItem>();
  categories.forEach((category) => {
    category.audiences.forEach((audience) => {
      map.set(audience.code, audience);
    });
  });
  return map;
};

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

type SelectionFractions = {
  segment: number;
  inTargetRatio: number;
};

const buildSelectionFractions = (audiences: SelectedAudience[]): SelectionFractions | null => {
  const categories = new Map<string, { segment: number; inTarget: number }>();

  for (const audience of audiences) {
    const segment = audience.segmentReach;
    const inTarget = audience.inTargetReach ?? audience.reachStats?.in_target_reach;
    if (
      segment == null
      || inTarget == null
      || !Number.isFinite(segment)
      || !Number.isFinite(inTarget)
    ) {
      return null;
    }

    // Segments in the same category are combined with OR.
    const current = categories.get(audience.category) ?? { segment: 0, inTarget: 0 };
    const segmentFraction = clamp(segment, 0, 1);
    current.segment += segmentFraction;
    current.inTarget += segmentFraction * clamp(inTarget, 0, 1);
    categories.set(audience.category, current);
  }

  if (!categories.size) return null;

  let segment = 1;
  let inTargetRatio = 1;
  // Different categories are combined with AND.
  for (const category of Array.from(categories.values())) {
    const categorySegment = clamp(category.segment, 0, 1);
    if (categorySegment === 0) return { segment: 0, inTargetRatio: 0 };

    segment *= categorySegment;
    inTargetRatio = Math.min(
      inTargetRatio,
      clamp(category.inTarget, 0, categorySegment) / categorySegment,
    );
  }

  return { segment, inTargetRatio };
};

const coreAudienceIndex = (code: string) => {
  const index = CORE_AUDIENCE_ORDER.indexOf(code);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

export const getCategoryTheme = (category: AudienceCategoryItem) => {
  const prefix = audienceCodePrefix(category.audiences[0]?.code ?? category.code);
  const order = CORE_DIMENSION_ORDER.indexOf(prefix);
  return {
    prefix,
    order: order === -1 ? Number.MAX_SAFE_INTEGER : order,
    color: CORE_DIMENSION_COLORS[prefix] ?? 'gray',
  };
};

export const getCoreAudienceOrder = (code: string) => coreAudienceIndex(code);

export const getCoreAudienceColor = (code: string) => {
  const prefix = audienceCodePrefix(code);
  return CORE_DIMENSION_COLORS[prefix] ?? 'gray';
};

export const getAudienceColorStep = (code: string) => {
  const index = AUDIENCE_COLOR_INDEX.get(code) ?? 0;
  return AUDIENCE_COLOR_STEPS[index % AUDIENCE_COLOR_STEPS.length];
};

const SYSTEM_NODE_STYLES: Record<string, { color: string; colorStep: number }> = {
  total: { color: 'sky', colorStep: 5 },
  selection: { color: 'orange', colorStep: 9 },
  inTarget: { color: 'yellow', colorStep: 9 },
  excess: { color: 'green', colorStep: 8 },
  active: { color: 'green', colorStep: 9 },
  suppressed: { color: 'red', colorStep: 9 },
};

export const filterCoreAudienceCategories = (categories: AudienceCategoryItem[]) => {
  const filtered = categories
    .map((category) => ({
      ...category,
      audiences: category.audiences
        .filter((audience) => CORE_AUDIENCE_CODES.has(audience.code))
        .sort((a, b) => coreAudienceIndex(a.code) - coreAudienceIndex(b.code)),
    }))
    .filter((category) => category.audiences.length > 0);

  return filtered.sort((a, b) => {
    const aPrefix = audienceCodePrefix(a.audiences[0]?.code ?? '');
    const bPrefix = audienceCodePrefix(b.audiences[0]?.code ?? '');
    const aIndex = CORE_DIMENSION_ORDER.indexOf(aPrefix);
    const bIndex = CORE_DIMENSION_ORDER.indexOf(bPrefix);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
};

export const sumMetric = (audiences: SelectedAudience[], key: ReachMetricKey) => (
  audiences.reduce((sum, audience) => sum + (audience.reachStats?.[key] ?? 0), 0)
);

export const sumByCategory = (audiences: SelectedAudience[], key: ReachMetricKey) => {
  const map = new Map<string, number>();
  audiences.forEach((audience) => {
    const value = audience.reachStats?.[key] ?? 0;
    const existing = map.get(audience.category) ?? 0;
    map.set(audience.category, existing + value);
  });
  return map;
};

export const selectionTotalFor = (audiences: SelectedAudience[], key: ReachMetricKey) => {
  const sums = Array.from(sumByCategory(audiences, key).values()).filter((value) => value > 0);
  if (!sums.length) return 0;
  if (sums.length === 1) return sums[0];
  return Math.min(...sums);
};

export const weightedInTargetRatio = (audiences: SelectedAudience[]) => {
  const total = sumMetric(audiences, 'num_daily_impressions');
  if (!total) return 1;
  const weighted = audiences.reduce((sum, audience) => {
    const value = audience.reachStats?.num_daily_impressions ?? 0;
    const ratio = clamp(audience.reachStats?.in_target_reach ?? 1, 0, 1);
    return sum + value * ratio;
  }, 0);
  return clamp(weighted / total, 0, 1);
};

const selectionIndexForAudience = (audience: SelectedAudience) => {
  const segmentReach = audience.segmentReach;
  const inTargetReach = audience.inTargetReach;
  if (segmentReach == null || inTargetReach == null || segmentReach <= 0) {
    return null;
  }
  return (inTargetReach / segmentReach) * 100;
};

export const getAudienceImpressions = (audience: SelectedAudience) => (
  audience.reachStats?.num_daily_impressions ?? 0
);

export const buildReachAnalysis = (
  audiences: SelectedAudience[],
  selectedCountry: CountryItem | undefined,
): ReachAnalysis => {
  const hasSelection = audiences.length > 0;
  const total = selectedCountry?.reach_stats;
  const fractions = buildSelectionFractions(audiences);
  const ratio = fractions?.inTargetRatio ?? weightedInTargetRatio(audiences);

  const metric = (key: ReachMetricKey) => {
    const totalValue = total?.[key];
    const selectionTotal = fractions && totalValue != null
      ? totalValue * fractions.segment
      : selectionTotalFor(audiences, key);
    return {
      total: totalValue,
      segment: hasSelection ? selectionTotal : undefined,
      inTarget: hasSelection ? selectionTotal * ratio : undefined,
    };
  };

  const selectionIndices = audiences
    .map(selectionIndexForAudience)
    .filter((value): value is number => Number.isFinite(value));
  const selectionIndex = hasSelection && selectionIndices.length
    ? Math.floor(selectionIndices.reduce((sum, value) => sum + value, 0) / selectionIndices.length)
    : 100;

  return {
    households: metric('num_households'),
    persons: metric('num_persons'),
    devices: metric('num_devices'),
    impressions: metric('num_daily_impressions'),
    selectionIndex,
  };
};

export const buildSankeyData = (
  audiences: SelectedAudience[],
  selectedCountry: CountryItem | undefined,
  locale?: string,
): SankeyData => {
  if (!audiences.length) return { links: [], nodes: [] };

  const maxSuppressedShare = 0.12;
  const totalImpressions = selectedCountry?.reach_stats?.num_daily_impressions ?? 0;
  const weights = audiences
    .map((audience) => ({
      id: `aud-${audience.code}`,
      code: audience.code,
      label: audience.label,
      category: audience.category,
      value: getAudienceImpressions(audience),
      color: getCoreAudienceColor(audience.code),
      order: getCoreAudienceOrder(audience.code),
    }))
    .filter((item) => item.value > 0)
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.label.localeCompare(b.label);
    });

  const weightedWithSteps = weights.map((item) => ({
    ...item,
    colorStep: getAudienceColorStep(item.code),
  }));

  const fractions = buildSelectionFractions(audiences);
  const selectionTotal = fractions && totalImpressions > 0
    ? totalImpressions * fractions.segment
    : selectionTotalFor(audiences, 'num_daily_impressions');
  const total = totalImpressions > 0 ? totalImpressions : selectionTotal;
  const ratio = fractions?.inTargetRatio ?? weightedInTargetRatio(audiences);
  const inTargetTotal = selectionTotal * ratio;
  const suppressed = Math.max(total - selectionTotal, 0);
  const suppressedDisplay = Math.min(
    suppressed,
    total * maxSuppressedShare,
    selectionTotal * 0.8,
  );
  const excess = Math.max(selectionTotal - inTargetTotal, 0);
  const suppressedOrder = Number.MAX_SAFE_INTEGER;

  const getSystemNodeStyle = (id: keyof typeof SYSTEM_NODE_STYLES) => SYSTEM_NODE_STYLES[id];
  const nodes: SankeyNode[] = [
    {
      id: 'total',
      name: `Total ${formatLargeNumber(total, locale)}`,
      order: 0,
      ...getSystemNodeStyle('total'),
    },
    {
      id: 'selection',
      name: `Audience Selection ${formatLargeNumber(selectionTotal, locale)}`,
      order: 0,
      ...getSystemNodeStyle('selection'),
    },
    {
      id: 'inTarget',
      name: `In-target ${formatLargeNumber(inTargetTotal, locale)}`,
      order: 0,
      ...getSystemNodeStyle('inTarget'),
    },
    {
      id: 'active',
      name: `Active ${formatLargeNumber(selectionTotal, locale)}`,
      order: 0,
      ...getSystemNodeStyle('active'),
    },
    {
      id: 'suppressed',
      name: `Suppressed ${formatLargeNumber(suppressed, locale)}`,
      order: suppressedOrder,
      ...getSystemNodeStyle('suppressed'),
    },
    {
      id: 'excess',
      name: `Excess ${formatLargeNumber(excess, locale)}`,
      order: 1,
      ...getSystemNodeStyle('excess'),
    },
  ];

  weightedWithSteps.forEach((item) => {
    nodes.push({
      id: item.id,
      name: `${item.category}\n${item.label} ${formatLargeNumber(item.value, locale)}`,
      order: item.order,
      color: item.color,
      colorStep: item.colorStep,
    });
  });

  const links: SankeyLink[] = [];
  const segmentTotal = weightedWithSteps.reduce((sum, item) => sum + item.value, 0);
  const segmentScale = segmentTotal > 0 && selectionTotal > 0
    ? selectionTotal / segmentTotal
    : 1;

  weightedWithSteps.forEach((item) => {
    const scaledValue = item.value * segmentScale;
    if (scaledValue <= 0) return;
    links.push({
      from: 'total',
      to: item.id,
      value: scaledValue,
      color: item.color,
      colorStep: item.colorStep,
    });
    links.push({
      from: item.id,
      to: 'selection',
      value: scaledValue,
      color: item.color,
      colorStep: item.colorStep,
    });
  });

  if (suppressed > 0) {
    const suppressedStyle = getSystemNodeStyle('suppressed');
    links.push({
      from: 'total',
      to: 'suppressed',
      value: suppressedDisplay,
      color: suppressedStyle.color,
      colorStep: Math.min(suppressedStyle.colorStep, 7),
    });
  }
  if (inTargetTotal > 0) {
    const inTargetStyle = getSystemNodeStyle('inTarget');
    links.push({
      from: 'selection',
      to: 'inTarget',
      value: inTargetTotal,
      color: inTargetStyle.color,
      colorStep: Math.min(inTargetStyle.colorStep, 7),
    });
    links.push({
      from: 'inTarget',
      to: 'active',
      value: inTargetTotal,
      color: inTargetStyle.color,
      colorStep: Math.min(inTargetStyle.colorStep, 7),
    });
  }
  if (excess > 0) {
    const excessStyle = getSystemNodeStyle('excess');
    links.push({
      from: 'selection',
      to: 'excess',
      value: excess,
      color: excessStyle.color,
      colorStep: Math.min(excessStyle.colorStep, 7),
    });
    links.push({
      from: 'excess',
      to: 'active',
      value: excess,
      color: excessStyle.color,
      colorStep: Math.min(excessStyle.colorStep, 7),
    });
  }

  const usedNodeIds = new Set<string>();
  links.forEach((link) => {
    usedNodeIds.add(link.from);
    usedNodeIds.add(link.to);
  });

  const nodeMap = new Map(nodes.map((node) => [node.id, node]));
  const finalNodes = Array.from(usedNodeIds).map((id) => (
    nodeMap.get(id) ?? { id, name: id }
  ));

  return {
    links,
    nodes: finalNodes,
  };
};
